import React, { useState } from "react";
import {
 Modal,
 Row,
 Col,
 Badge,
 Table,
 Button,
 Form,
 Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { updateObservation } from "../../../services/qcService";
import { getFileUrl } from "../../../api";
import PermissionWrapper from "../PermissionWrapper";

const STATUS_COLORS = {
 Draft: "secondary",
 Submitted: "warning",
 Approved: "success",
 Rejected: "danger",
 Closed: "dark",
};

const RESULT_COLORS = {
 Pass: "success",
 Fail: "danger",
 "Conditional Pass": "warning",
 Pending: "secondary",
 NA: "light",
};

const QCViewModal = ({ show, record, onHide, onRefresh, user }) => {
 const navigate = useNavigate();
 const [editingObs, setEditingObs] = useState(null); // { idx, obsId, result, observedValue, remarks }
 const [savingObs, setSavingObs] = useState(false);

 if (!record) return null;

 const startEditObs = (obs, idx) => {
 setEditingObs({
 idx,
 obsId: obs._id,
 result: obs.result,
 observedValue: obs.observedValue || "",
 remarks: obs.remarks || "",
 });
 };

 const cancelEditObs = () => setEditingObs(null);

 const saveObs = async () => {
 if (!editingObs) return;
 setSavingObs(true);
 try {
 await updateObservation(record._id, editingObs.obsId, {
 result: editingObs.result,
 observedValue: editingObs.observedValue,
 remarks: editingObs.remarks,
 });
 toast.success("Observation updated");
 setEditingObs(null);
 onRefresh && onRefresh();
 onHide();
 } catch (_) {
 toast.error("Failed to update observation");
 } finally {
 setSavingObs(false);
 }
 };

 const infoRow = (label, value) => (
 <Col md={4} xs={6} className="mb-3">
 <div className="text-muted" style={{ fontSize: "0.78rem", letterSpacing: 0.5 }}>
 {label}
 </div>
 <div className="fw-semibold">{value || "-"}</div>
 </Col>
 );

 const ncrReports = Array.isArray(record.ncrReports) && record.ncrReports.length
 ? record.ncrReports
 : record.nonConformanceRaised
 ? [{
 details: record.nonConformanceDetails || "",
 correctiveAction: record.correctiveAction || "",
 remarks: record.remarks || "",
 closedDate: record.ncrClosedDate || null,
 raisePhotos: [],
 closePhotos: [],
 }]
 : [];

 return (
 <Modal show={show} onHide={onHide} size="xl" centered scrollable>
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title className="d-flex align-items-center gap-2">
 <i className="fas fa-microscope text-primary" />
 <span>QC Inspection - {record.qcNumber}</span>
 <Badge bg={STATUS_COLORS[record.status] || "secondary"} className="ms-1">
 {record.status}
 </Badge>
 <Badge bg={RESULT_COLORS[record.overallResult] || "secondary"}>
 {record.overallResult}
 </Badge>
 {record.nonConformanceRaised && (
 <Badge bg="danger">NCR</Badge>
 )}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {/* â”€â”€ Core Info â”€â”€ */}
 <h6
 className="text-uppercase text-muted mb-3"
 style={{ fontSize: "0.75rem", letterSpacing: 1 }}
 >
 Inspection Details
 </h6>
 <Row className="mb-3">
 {infoRow("QC Number", record.qcNumber)}
 {infoRow(
 "Inspection Date",
 record.inspectionDate
 ? new Date(record.inspectionDate).toLocaleDateString("en-IN")
 : "-"
 )}
 {infoRow("Type", record.inspectionType)}
 {infoRow("Activity / Scope", record.activityOrScope)}
 {infoRow("Location", record.location)}
 {infoRow("Reference Standard", record.referenceStandard)}
 {infoRow("Project", record.project?.projectName || record.project?.name)}
 {infoRow(
 "Work Order",
 record.workOrder
 ? `${record.workOrder.woNo} - ${record.workOrder.scope || ""}`
 : null
 )}
 {infoRow("Inspected By", record.inspectedBy?.name)}
 {infoRow("Created By", record.createdBy?.name)}
 {infoRow(
 "Next Inspection",
 record.nextInspectionDate
 ? new Date(record.nextInspectionDate).toLocaleDateString("en-IN")
 : null
 )}
 {record.remarks && infoRow("Remarks", record.remarks)}
 </Row>

 {/* â”€â”€ Observations â”€â”€ */}
 {record.observations?.length > 0 && (
 <>
 <hr />
 <div className="d-flex justify-content-between align-items-center mb-2">
 <h6
 className="text-uppercase text-muted mb-0"
 style={{ fontSize: "0.75rem", letterSpacing: 1 }}
 >
 Observations / Test Results
 </h6>
 </div>
 <div className="table-responsive">
 <Table bordered size="sm" className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Parameter</th>
 <th>Specified</th>
 <th>Observed</th>
 <th>Unit</th>
 <th>Result</th>
 <th>Remarks</th>
 <PermissionWrapper
 user={user}
 required={["edit:qc"]}
 mode="some"
 >
 <th style={{ width: 80 }}>Edit</th>
 </PermissionWrapper>
 </tr>
 </thead>
 <tbody>
 {record.observations.map((obs, idx) => {
 const isEditing = editingObs?.idx === idx;
 return (
 <tr key={obs._id || idx}>
 <td>{idx + 1}</td>
 <td className="fw-semibold">{obs.parameter}</td>
 <td>{obs.specifiedValue || "-"}</td>
 <td>
 {isEditing ? (
 <Form.Control
 size="sm"
 value={editingObs.observedValue}
 onChange={(e) =>
 setEditingObs((prev) => ({
 ...prev,
 observedValue: e.target.value,
 }))
 }
 style={{ width: 100 }}
 />
 ) : (
 obs.observedValue || "-"
 )}
 </td>
 <td>{obs.unit || "-"}</td>
 <td>
 {isEditing ? (
 <Form.Select
 size="sm"
 value={editingObs.result}
 onChange={(e) =>
 setEditingObs((prev) => ({
 ...prev,
 result: e.target.value,
 }))
 }
 style={{ width: 100 }}
 >
 {["Pending", "Pass", "Fail", "NA"].map((r) => (
 <option key={r} value={r}>{r}</option>
 ))}
 </Form.Select>
 ) : (
 <Badge bg={RESULT_COLORS[obs.result] || "secondary"}>
 {obs.result}
 </Badge>
 )}
 </td>
 <td>
 {isEditing ? (
 <Form.Control
 size="sm"
 value={editingObs.remarks}
 onChange={(e) =>
 setEditingObs((prev) => ({
 ...prev,
 remarks: e.target.value,
 }))
 }
 />
 ) : (
 obs.remarks || "-"
 )}
 </td>
 <PermissionWrapper
 user={user}
 required={["edit:qc"]}
 mode="some"
 >
 <td>
 {isEditing ? (
 <div className="d-flex gap-1">
 <Button
 size="sm"
 variant="success"
 onClick={saveObs}
 disabled={savingObs}
 >
 {savingObs ? (
 <Spinner size="sm" />
 ) : (
 <i className="fas fa-check" />
 )}
 </Button>
 <Button
 size="sm"
 variant="outline-secondary"
 onClick={cancelEditObs}
 >
 <i className="fas fa-times" />
 </Button>
 </div>
 ) : (
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => startEditObs(obs, idx)}
 >
 <i className="fas fa-edit" />
 </Button>
 )}
 </td>
 </PermissionWrapper>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {/* â”€â”€ NCR â”€â”€ */}
 {record.nonConformanceRaised && (
 <>
 <hr />
 <h6
 className="text-uppercase text-muted mb-3"
 style={{ fontSize: "0.75rem", letterSpacing: 1 }}
 >
 <i className="fas fa-exclamation-triangle me-1 text-danger" />
 Non-Conformance Reports ({ncrReports.length})
 </h6>
 <div className="d-flex flex-column gap-3">
 {ncrReports.map((report, idx) => (
 <div key={idx} className="border rounded p-3 bg-light">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <strong>NCR Report {idx + 1}</strong>
 <Badge bg={report.closedDate ? "success" : "danger"}>
 {report.closedDate ? "Closed" : "Open"}
 </Badge>
 </div>
 <Row>
 {infoRow("NCR Details", report.details)}
 {infoRow("Corrective Action", report.correctiveAction)}
 {infoRow(
 "Closed Date",
 report.closedDate
 ? new Date(report.closedDate).toLocaleDateString("en-IN")
 : "Open"
 )}
 {report.remarks && infoRow("Remarks", report.remarks)}
 </Row>
 {(report.raisePhotos?.length > 0 || report.closePhotos?.length > 0) && (
 <div className="mt-3">
 {report.raisePhotos?.length > 0 && (
 <>
 <div className="fw-semibold mb-2">Raise Time Images</div>
 <div className="d-flex flex-wrap gap-2 mb-3">
 {report.raisePhotos.map((photo) => (
 <a
 key={photo._id || photo.fileName}
 href={getFileUrl(photo.fileUrl)}
 target="_blank"
 rel="noreferrer"
 >
 <img
 src={getFileUrl(photo.fileUrl)}
 alt={photo.description || photo.fileName}
 className="rounded border"
 style={{ width: 110, height: 90, objectFit: "cover" }}
 onError={(e) => {
 e.currentTarget.onerror = null;
 e.currentTarget.src = "/logo192.png";
 }}
 />
 </a>
 ))}
 </div>
 </>
 )}
 {report.closePhotos?.length > 0 && (
 <>
 <div className="fw-semibold mb-2">Close Time Images</div>
 <div className="d-flex flex-wrap gap-2">
 {report.closePhotos.map((photo) => (
 <a
 key={photo._id || photo.fileName}
 href={getFileUrl(photo.fileUrl)}
 target="_blank"
 rel="noreferrer"
 >
 <img
 src={getFileUrl(photo.fileUrl)}
 alt={photo.description || photo.fileName}
 className="rounded border"
 style={{ width: 110, height: 90, objectFit: "cover" }}
 onError={(e) => {
 e.currentTarget.onerror = null;
 e.currentTarget.src = "/logo192.png";
 }}
 />
 </a>
 ))}
 </div>
 </>
 )}
 </div>
 )}
 </div>
 ))}
 </div>
 </>
 )}

 {/* â”€â”€ Approval Info â”€â”€ */}
 {(record.status === "Approved" || record.status === "Rejected") && (
 <>
 <hr />
 <h6
 className="text-uppercase text-muted mb-3"
 style={{ fontSize: "0.75rem", letterSpacing: 1 }}
 >
 Approval Information
 </h6>
 <Row>
 {infoRow(
 "Approved / Rejected By",
 record.approvedBy?.name
 )}
 {infoRow(
 "Date",
 record.approvalDate
 ? new Date(record.approvalDate).toLocaleDateString("en-IN")
 : null
 )}
 {infoRow("Remarks", record.approvalRemarks)}
 </Row>
 </>
 )}

 {/* â”€â”€ Photos â”€â”€ */}
 </Modal.Body>

 <Modal.Footer>
 <PermissionWrapper
 user={user}
 required={["edit:qc"]}
 mode="some"
 >
 <Button
 variant="outline-primary"
 size="sm"
 onClick={() => {
 onHide();
 navigate(`/qc/edit/${record._id}`);
 }}
 >
 <i className="fas fa-edit me-1" /> Edit
 </Button>
 </PermissionWrapper>
 <Button variant="secondary" onClick={onHide}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 );
};

export default QCViewModal;

